import { useEffect } from 'react';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- 롤링 digit은 고정폭/height 정밀 제어가 필요해 Typography로 대체 불가
import { StyleSheet, Text, View, type TextStyle } from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

const DIGITS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

export type RollingMode = 'countdown' | 'price';

const MODE_CONFIG = {
  // 카운트다운: 매초 바뀐 자릿수만 위→아래 슬라이드
  countdown: { duration: 250, easing: Easing.inOut(Easing.ease) },
  // 가격: 노출 시 0→목표 위로 롤업 (1회)
  price: { duration: 500, easing: Easing.out(Easing.ease) },
} as const;

type RollingDigitProps = {
  value: number;
  digitHeight: number;
  textStyle: TextStyle;
  mode: RollingMode;
  /**
   * price 모드 전용.
   * - false: 목표 숫자에 고정 (마운트 유지, 깜빡임 없음)
   * - true: 0→목표 롤업. false→true로 바뀌면 재재생
   */
  play: boolean;
};

const RollingDigit = ({
  value,
  digitHeight,
  textStyle,
  mode,
  play,
}: RollingDigitProps) => {
  const config = MODE_CONFIG[mode];
  // price+play: 0에서 시작. 그 외(countdown / price+!play)는 목표 위치에 고정
  const translateY = useSharedValue(
    mode === 'price' && play ? 0 : -value * digitHeight,
  );

  useEffect(() => {
    if (mode === 'price') {
      if (play) {
        // 0으로 되돌린 뒤 목표까지 롤업
        translateY.value = 0;
        translateY.value = withTiming(-value * digitHeight, {
          duration: config.duration,
          easing: config.easing,
        });
      } else {
        // play=false 중 목표가 바뀌어도 애니메이션 없이 금액 유지
        translateY.value = -value * digitHeight;
      }
      return;
    }

    translateY.value = withTiming(-value * digitHeight, {
      duration: config.duration,
      easing: config.easing,
    });
  }, [value, digitHeight, config, translateY, mode, play]);

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: translateY.value }],
  }));

  return (
    <View style={{ height: digitHeight, overflow: 'hidden' }}>
      <Animated.View style={animatedStyle}>
        {DIGITS.map(digit => (
          <Text
            key={digit}
            allowFontScaling={false}
            style={[styles.digit, textStyle, { height: digitHeight }]}
          >
            {digit}
          </Text>
        ))}
      </Animated.View>
    </View>
  );
};

type RollingNumberTextProps = {
  /** 예: "23:59:58"(countdown) 또는 "11,000"(price) */
  text: string;
  digitHeight: number;
  textStyle: TextStyle;
  mode: RollingMode;
  /**
   * price 모드에서만 의미 있음. false면 목표 고정, true면 0→목표 롤업.
   * countdown에서는 무시(항상 자리 변경 애니메이션).
   * @default true
   */
  play?: boolean;
};

type DigitRun = { text: string; start: number; isDigit: boolean };

/** 문자열을 연속 숫자런/비숫자런으로 분할. 숫자런은 자리별 롤링, 비숫자런은 통짜 렌더에 쓴다. */
const splitDigitRuns = (text: string): DigitRun[] => {
  const runs: DigitRun[] = [];
  let i = 0;
  while (i < text.length) {
    const isDigit = /\d/.test(text[i]);
    let j = i;
    while (j < text.length && /\d/.test(text[j]) === isDigit) j += 1;
    runs.push({ text: text.slice(i, j), start: i, isDigit });
    i = j;
  }
  return runs;
};

/**
 * 문자열의 숫자 자리는 세로 롤링(reanimated), 비숫자런(콜론·콤마·' 남음' 등)은 통짜로 정적 렌더해 자간을 유지한다.
 * 웹·앱 공용. 자릿수 고정 문자열(HH:MM:SS, 금액)에 사용 — 자릿수가 변하면 key 재사용으로 깜빡일 수 있음.
 */
export const RollingNumberText = ({
  text,
  digitHeight,
  textStyle,
  mode,
  play = true,
}: RollingNumberTextProps) => {
  return (
    <View style={styles.row}>
      {splitDigitRuns(text).map(run =>
        run.isDigit ? (
          run.text
            .split('')
            .map((char, offset) => (
              <RollingDigit
                key={run.start + offset}
                value={Number(char)}
                digitHeight={digitHeight}
                textStyle={textStyle}
                mode={mode}
                play={play}
              />
            ))
        ) : (
          <Text
            key={run.start}
            allowFontScaling={false}
            style={[styles.digit, textStyle, { height: digitHeight }]}
          >
            {run.text}
          </Text>
        ),
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  row: { flexDirection: 'row' },
  digit: { textAlign: 'center', fontVariant: ['tabular-nums'] },
});
